import { FileDocIcon, TrashIcon, UploadIcon } from '@velocity/icons/system';
import { LinkStandalone, ProgressBar, Text } from '@velocity/ui';
import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getDocument } from '../../../../api/instant-lease-api';
import { setNotification } from '../../../../redux/cache-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import { Document } from '../../../../types/instant-lease-api';
import { getText } from '../../../../utils/getter';

interface UploadDropzoneProps {
  documentType: string;
  document?: Document;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDelete?: () => void;
  uploadProgress?: number;
  error?: string;
  isLoading?: boolean;
}

const shortenFileName = (filename: string): string =>
  filename.length <= 40
    ? filename
    : `${filename.substring(0, 18)}...${filename.substring(filename.length - 19)}`;

/*
 * B2C document upload (Figma: "document upload" desktop): dashed drop area with
 * drag & drop + "Datei hochladen" link, or the uploaded file with a remove link.
 */
const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  documentType,
  document,
  onChange,
  onDelete,
  uploadProgress = 0,
  error,
  isLoading = false,
}) => {
  const dispatch = useAppDispatch();
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );
  const { accessToken } = useSelector((state: RootState) => state.auth);

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const inputId = `${documentType.toLowerCase()}-input`;
  const uploading = uploadProgress > 0 && uploadProgress < 100;

  const selectFile = () => inputRef.current?.click();

  // Hands dropped files to the native input so `onChange` receives a regular change event
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const { files } = event.dataTransfer;
    if (!inputRef.current || !files?.length) return;
    inputRef.current.files = files;
    inputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const downloadFile = () => {
    if (!document) return;
    const { mime_type, token, file_name } = document;
    getDocument(accessToken, activeApplication?.uuid || '', token)
      .then((response) => {
        const blob = window.URL.createObjectURL(
          new Blob([response], { type: mime_type }),
        );
        const link = window.document.createElement('a');
        link.href = blob;
        link.setAttribute('download', file_name);
        link.click();
        window.open(blob, '_blank');
      })
      .catch(() => {
        dispatch(
          setNotification({
            notificationVisibility: true,
            notificationHasError: true,
            notificationTitle: 'Das tut uns leid',
            notificationBody: getText('error_unable_to_process_data'),
          }),
        );
      });
  };

  if (document) {
    return (
      <div className='flex w-full items-center justify-between gap-4 rounded-lg border border-(--vel-color-secondary-100) bg-white px-4 py-3'>
        <div
          className='flex min-w-0 items-center gap-3'
          data-testid='file_name'
        >
          <FileDocIcon size='m' data-testid='uploaded-icon' />
          <button
            type='button'
            className='cursor-pointer truncate border-0 bg-transparent p-0 text-left'
            onClick={downloadFile}
          >
            <Text component='span' variant='200' bold>
              {shortenFileName(document.file_name)}
            </Text>
          </button>
        </div>
        <LinkStandalone
          component='button'
          type='button'
          Icon={TrashIcon}
          className='text-(--vel-color-semantic-error-high,#cf3516)! [&_svg_path]:fill-current'
          onClick={onDelete}
          data-testid='delete-icon'
        >
          Datei entfernen
        </LinkStandalone>
      </div>
    );
  }

  return (
    <div className='flex w-full flex-col gap-2'>
      <div
        data-testid='upload-dropzone'
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`flex min-h-[166px] flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-4 py-6 text-center ${
          error
            ? 'border-(--vel-color-semantic-error-high)'
            : 'border-(--vel-color-secondary-300)'
        } ${dragging ? 'bg-(--vel-color-secondary-100)' : 'bg-(--vel-color-secondary-50)'}`}
      >
        {uploading ? (
          <div className='w-full max-w-xs'>
            <ProgressBar
              aria-label='Upload progress'
              variant='neutral'
              percent={uploadProgress}
            />
            <Text component='p' variant='100'>
              Uploading: {uploadProgress}%
            </Text>
          </div>
        ) : (
          <>
            <FileDocIcon size='l' data-testid='upload-box-icon' />
            <Text component='p' variant='300'>
              Drag &amp; Drop
            </Text>
            <Text component='p' variant='100'>
              ziehe deine Dateien einfach in das Feld oder
            </Text>
            <LinkStandalone
              component='button'
              type='button'
              Icon={UploadIcon}
              onClick={selectFile}
              disabled={isLoading}
              data-testid='upload-icon'
            >
              Datei hochladen
            </LinkStandalone>
          </>
        )}
        <input
          ref={inputRef}
          id={inputId}
          data-testid={inputId}
          name={documentType.toLowerCase()}
          type='file'
          className='sr-only'
          tabIndex={-1}
          onChange={onChange}
        />
      </div>
      <Text component='p' variant='100'>
        JPG, PNG, PDF, max. 5 MB, höchstens 6 Monate alt.
      </Text>
    </div>
  );
};

export default UploadDropzone;
